import { LinearUnit } from '../../utils/proj4/units';
import { clamp } from '../utilities/math-helpers';
import { ViewerEnvironmentInstance } from '../viewer-environment';

/**
 * Per second, in the scene's unit, which is the project's: about as fast in meters as in feet, in
 * round numbers.
 */
const baseSpeeds: Record<LinearUnit, number> = { m: 10, ft: 30 };
/** How much faster or slower each step, e.g. a notch of the wheel, makes it. */
const stepFactor = 1.25;
const minFactor = 1 / 16;
const maxFactor = 16;

type Listener = () => void;

/**
 * How fast the camera flies in free flight, kept while it orbits in between. Changing it notifies
 * the listeners, which show it.
 */
export class FlySpeed {
  private static _instance: FlySpeed | undefined;

  static get instance() {
    if (!FlySpeed._instance) {
      FlySpeed._instance = new FlySpeed();
    }
    return FlySpeed._instance;
  }

  private factor = 1;
  private readonly listeners = new Set<Listener>();

  get unit(): LinearUnit {
    return ViewerEnvironmentInstance.getSpatialReference().linearUnit ?? 'm';
  }

  /** Per second, in the scene's unit. */
  get speed() {
    return baseSpeeds[this.unit] * this.factor;
  }

  /** Faster for steps above 0, slower below. */
  change(steps: number) {
    this.factor = clamp(this.factor * stepFactor ** steps, minFactor, maxFactor);
    for (const listener of this.listeners) {
      listener();
    }
  }

  reset() {
    this.factor = 1;
  }

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}
